import React, { useEffect, useState } from 'react';
import Header from './Header';
import { apiService, PrimeReport } from '../services/api';
import { useParams, Link } from 'react-router-dom';

const PrimeReportDetailView: React.FC = () => {
  const { reportId } = useParams();
  const [report, setReport] = useState<PrimeReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!reportId) {
          setError('Missing report id');
          return;
        }
        const result = await apiService.fetchPrimeReportDetail(Number(reportId));
        if (result?.success) {
          setReport(result.report);
        } else {
          setError(result?.error || 'Unable to load report');
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Unable to load report');
      } finally {
        setLoading(false);
      }
    };
    loadReport();
  }, [reportId]);

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDrivePreviewUrl = (link?: string) => {
    if (!link) return '';
    const match = link.match(/\/file\/d\/([^/]+)/);
    const fileId = match ? match[1] : link.split('/').pop();
    return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : link;
  };

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />
      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8">
        {loading ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-12">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8 text-center text-rose-600">
            {error}
          </div>
        ) : report ? (
          <>
            <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">{report.title}</h1>
                  <p className="text-sm text-slate-500 mt-2">
                    Purchased {formatDate(report.purchased_at)} by {report.purchased_by || '—'}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Link
                    to="/prime-reports"
                    className="px-4 py-2 text-xs font-semibold rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    Back to Reports
                  </Link>
                  {report.google_drive_link && (
                    <a
                      href={report.google_drive_link}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 text-xs font-semibold rounded-full bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      Download PDF
                    </a>
                  )}
                </div>
              </div>
            </div>

            {report.google_drive_link && (
              <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-4">
                <p className="text-xs text-slate-500 px-4 pb-3">
                  Preview below. Download the PDF for full-quality resolution.
                </p>
                <div className="w-full">
                  <iframe
                    title="Prime report preview"
                    src={getDrivePreviewUrl(report.google_drive_link)}
                    className="w-full border-0"
                    style={{ height: '70vh' }}
                  />
                </div>
              </div>
            )}

            {report.description && (
              <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">About This Report</h2>
                <p className="text-sm text-slate-600">{report.description}</p>
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
};

export default PrimeReportDetailView;
